import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { createImageVariantConfig } from '../util/sdkLoader';

/**
 * Loads the listings behind the customer's saved ranking.
 *
 * Asks for exactly the saved ids, so the page is never a general listings
 * query. An id that no longer comes back (sold, unpublished, deleted) is simply
 * absent from the result; the ranking keeps it and the panel shows it as no
 * longer available.
 */

// The most ids one query carries. The saved list is capped well below this, so
// a single request always covers it.
const MAX_IDS_PER_QUERY = 25;

export const fetchSavedVehiclesPayloadCreator = async (ids, thunkAPI) => {
  const { extra: sdk, rejectWithValue } = thunkAPI;
  const requested = (ids || []).slice(0, MAX_IDS_PER_QUERY);

  if (requested.length === 0) return {};

  try {
    const response = await sdk.listings.query({
      ids: requested,
      include: ['images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
      ...createImageVariantConfig('landscape-crop', 400, 0.75),
      ...createImageVariantConfig('landscape-crop2x', 800, 0.75),
      perPage: MAX_IDS_PER_QUERY,
    });

    const listings = denormalisedResponseEntities(response);
    return listings.reduce((byId, listing) => {
      const id = listing?.id?.uuid;
      if (id) byId[id] = listing;
      return byId;
    }, {});
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchSavedVehiclesThunk = createAsyncThunk(
  'app/savedVehicles/fetch',
  fetchSavedVehiclesPayloadCreator
);

const initialState = {
  listingsById: {},
  // True once a fetch has finished successfully. Until then the page cannot tell a
  // vehicle that is gone from one whose listing simply has not arrived yet.
  loaded: false,
  fetchInProgress: false,
  fetchError: null,
};

const savedVehiclesSlice = createSlice({
  name: 'app/savedVehicles',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSavedVehiclesThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchSavedVehiclesThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.loaded = true;
        state.listingsById = action.payload;
      })
      .addCase(fetchSavedVehiclesThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      });
  },
});

export default savedVehiclesSlice.reducer;
